import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    useDisclosure,
    chakra,
    useToast,
} from '@chakra-ui/react';
import FileSaver from 'file-saver';

import { useExportNonAcceptedKeywordsMutation, useUploadClientKeywordsMutation } from 'core/API/keywordsApi';
import {
    useGetAuthorizedTabsSelector,
    useGetCompanyInfoSelector,
    useGetUserInfoSelector,
} from 'core/redux/selectors/authSelectors';
import PageHeader from 'layout/pageHeader';
import { linkStyle } from 'shared/styles';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { useTranslation } from 'helpers/languages/translations';
import { useTabNavigate } from 'router/tabsMapper';
import { useUploadProductKeywordsMutation } from 'core/API/productKeywordsApi';
import keys from 'config';
import MKeywordsViewer from './components/molecules/MKeywordsViewer';
import ProductKeywordsViewer from './components/molecules/ProductKeywordsViewer/ProductKeywordsViewer';
import KeywordCategoriesViewer from './components/molecules/KeywordCategoriesViewer';
import { uploadModalFields } from './constants';

const PATH = 'keywords-management';
const ID = 'cdm_keywords';
const NAME = 'page.keyword';
const displayKeywordCategoriesTab = keys.ENVIRONMENT !== 'production';

const KeywordsManagement = () => {
    const navigate = useNavigate();
    const companyInfo = useGetCompanyInfoSelector();
    const { authorizedTabs } = useGetAuthorizedTabsSelector();

    const hasEanKeywordsTabAccess = authorizedTabs.includes('product_search_score_v3');
    const displayKeywordEanTab = keys.ENVIRONMENT !== 'production' && hasEanKeywordsTabAccess;
    const { tabIndex, setTabIndex } = useTabNavigate('keywords-management', {
        'ean-keywords': displayKeywordEanTab,
        'keywords-categories': displayKeywordCategoriesTab,
    });

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(18);

    const translate = useTranslation();

    const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
    const {
        isOpen: isProductKeywordsImportOpen,
        onOpen: onProductKeywordsImportOpen,
        onClose: onProductKeywordsImportClose,
    } = useDisclosure();

    const uploadKeywordsData = useUploadClientKeywordsMutation({
        fixedCacheKey: 'keywords-upload-key',
    });

    const uploadProductKeywordsData = useUploadProductKeywordsMutation({
        fixedCacheKey: 'product-keywords-upload-key',
    });

    const { data: uploadedKeywords, isSuccess, reset } = uploadKeywordsData[1];
    const {
        data: uploadedProductKeywords,
        isSuccess: isProductKeywordsSuccess,
        reset: resetProductKeywords,
    } = uploadProductKeywordsData[1];
    const [exportNonAcceptedKeywords] = useExportNonAcceptedKeywordsMutation();
    const toast = useToast();

    useEffect(() => {
        if (isSuccess && uploadedKeywords) {
            onImportOpen();
        }
        if (isProductKeywordsSuccess && uploadedProductKeywords) {
            onProductKeywordsImportOpen();
        }
    }, [isSuccess, uploadedKeywords]);

    const handleCloseImportModal = () => {
        onImportClose();
        reset();
    };

    const handleCloseProductKeywordsImportModal = () => {
        onProductKeywordsImportClose();
        resetProductKeywords();
    };

    const handleDownloadNonAcceptedKeywords = async () => {
        /* eslint-disable  @typescript-eslint/no-non-null-assertion */
        try {
            const res = await exportNonAcceptedKeywords(uploadedKeywords!.notAccepted);
            if ('error' in res) throw new Error(translate('page.keyword.errorApiExport') as string);

            const blob = new Blob([res.data], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(
                blob,
                translate('page.keyword.exportWithErrorFileName', { name: companyInfo!.name_id }) as string,
            );
        } catch (error) {
            toast({
                title: (error as Error).message || translate('page.keyword.exportDefaultError'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderImportModal = () => {
        const { notAccepted, created, updated } = uploadedKeywords || { notAccepted: [], created: [], updated: [] };
        let usedFields = null;
        if (notAccepted.length === 0) usedFields = uploadModalFields.full;
        else if (created.length === 0 && updated.length === 0) usedFields = uploadModalFields.error;
        else usedFields = uploadModalFields.partial;

        return (
            <Modal isCentered isOpen={isImportOpen} onClose={handleCloseImportModal}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text color={created?.length === 0 && updated?.length === 0 ? 'red.600' : 'gray.900'} as="h3">
                            {usedFields.title}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" />
                    <ModalBody fontSize="sm">
                        <Text> {usedFields.subTitle} </Text>
                        {uploadedKeywords && (
                            <>
                                {created.length > 0 && (
                                    <Text>{translate('page.keyword.added', { nb: created.length })}</Text>
                                )}
                                {updated.length > 0 && (
                                    <Text>
                                        {translate('page.keyword.updated', {
                                            nb: uploadedKeywords?.updated?.length,
                                        })}
                                    </Text>
                                )}
                                {notAccepted.length > 0 && (
                                    <Text>
                                        {translate('page.keyword.errorsUploadedDescription', {
                                            nb: notAccepted.length,
                                            downloadComp: (
                                                <chakra.span
                                                    onClick={intentionalyFloatingPromiseReturn(
                                                        handleDownloadNonAcceptedKeywords,
                                                    )}
                                                    {...linkStyle}
                                                >
                                                    {translate('page.keyword.errorDownload')}
                                                </chakra.span>
                                            ),
                                        })}
                                    </Text>
                                )}
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={handleCloseImportModal} size="sm" colorScheme="blue" bg="blueMain">
                            {translate('page.keyword.errorModalNext')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderImportProductKeywordsModal = () => {
        return (
            <Modal isCentered isOpen={isProductKeywordsImportOpen} onClose={onProductKeywordsImportClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text color={'gray.900'} as="h3">
                            {translate('product_keyword.modal.uploadSuccessful.title')}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" />
                    <ModalBody fontSize="sm">
                        <Text mb="2">{translate('product_keyword.modal.uploadSuccessful.message')}.</Text>
                        {uploadedProductKeywords &&
                        uploadedProductKeywords?.notFoundKeywords &&
                        uploadedProductKeywords?.notFoundKeywords.length ? (
                            <>
                                <Text mb="2">{translate('product_keyword.modal.message.wrongKeywords')}:</Text>
                                <Text fontWeight="bold">
                                    {uploadedProductKeywords.notFoundKeywords.map((keyword: string, index: number) => (
                                        <Text key={keyword} as="span">
                                            {index === 0 ? ` ${keyword}` : `, ${keyword}`}
                                        </Text>
                                    ))}
                                </Text>
                            </>
                        ) : (
                            ''
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            onClick={handleCloseProductKeywordsImportModal}
                            size="sm"
                            colorScheme="blue"
                            bg="blueMain"
                        >
                            {translate('page.keyword.errorModalNext')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderTabs = () => (
        <Tabs size="sm" onChange={(index) => setTabIndex(index)} isLazy index={tabIndex}>
            <TabList>
                <Tab _focus={{ boxshadow: 'none' }}>{translate('tab.search')}</Tab>
                <Tab _focus={{ boxshadow: 'none' }}>{translate('tab.banner')}</Tab>
                {displayKeywordEanTab && <Tab _focus={{ boxshadow: 'none' }}> {translate('tab.ean_keywords')}</Tab>}
                {displayKeywordCategoriesTab && <Tab _focus={{ boxshadow: 'none' }}>{translate('tab.categories')}</Tab>}
            </TabList>
            <TabPanels>
                <TabPanel px="0">
                    <MKeywordsViewer keywordsType="search" readonly={readonly} userInfo={userInfo} />
                </TabPanel>
                <TabPanel px="0">
                    <MKeywordsViewer keywordsType="banner" readonly={readonly} userInfo={userInfo} />
                </TabPanel>
                {displayKeywordEanTab && (
                    <TabPanel px="0">
                        <ProductKeywordsViewer readonly={readonly} userInfo={userInfo} />
                    </TabPanel>
                )}
                {displayKeywordCategoriesTab && (
                    <TabPanel px="0">
                        <KeywordCategoriesViewer readonly={readonly} userInfo={userInfo} />
                    </TabPanel>
                )}
            </TabPanels>
        </Tabs>
    );

    const ArrowUpIcon = icons.arrowUp;
    const disableUpload = readonly;
    return (
        <Box>
            <PageHeader icon={{ name: 'keywords' }} pageTitle={translate(NAME) as string} readonly={readonly}>
                <Button
                    onClick={() => navigate('upload')}
                    fontSize="14px"
                    h="30px"
                    bg={'white'}
                    variant="outline"
                    leftIcon={<ArrowUpIcon />}
                    isDisabled={disableUpload}
                >
                    {translate('file.upload')}
                </Button>
            </PageHeader>
            <Box px="32px">
                {renderTabs()}
                {uploadedKeywords && renderImportModal()}
                {uploadedProductKeywords && renderImportProductKeywordsModal()}
            </Box>
        </Box>
    );
};

export { ID, PATH, NAME };
export default KeywordsManagement;
